import winfinityBg from '@/assets/auth/winfinityBackground.png'
import winfinityCoinIcon from '@/assets/tenants/winfinity/winfinityCoin.svg'

import type { SemanticTokens, TenantTheme } from '../types'

export const winfinityTheme: TenantTheme = {
  colors: {
    Base: {
      Black: '#0b0b0b',
      White: '#ffffff',
      Transparent: '#ffffff',
      'Black Blue': '#222232',
      'Gray Blue': '#55575a',
      'English Red': '#801112',
    },
    Gray: {
      '50': '#f3f3f3',
      '100': '#e8e8e8',
      '200': '#d1d1d1',
      '300': '#bcbcbc',
      '400': '#a5a5a5',
      '500': '#8f8f8f',
      '600': '#787879',
      '700': '#565756',
      '800': '#4b4b4b',
      '950': '#292929',
    },
    Red: {
      '50': '#fef2f2',
      '100': '#fee2e2',
      '200': '#fecaca',
      '300': '#fca5a5',
      '400': '#f87171',
      '500': '#ef4444',
      '600': '#dc2626',
      '700': '#b91c1c',
      '800': '#991b1b',
      '900': '#7f1d1d',
      '950': '#450a0a',
    },

    Brand: {
      primary: '#FAD26B',
      secondary: '#423D31',
    },
  },
  semanticTokens: {
    text: {
      base: { value: '#f3f3f3', description: 'Base text color' },
      content: { value: '#bcbcbc', description: 'Content text color' },
      error: { value: '#dc2626', description: 'Error text color' },
      inverse: { value: '#292929', description: 'Inverse text color' },
      primary: { value: '#e8e8e8', description: 'Primary text color' },
      secondary: { value: '#8f8f8f', description: 'Secondary text color' },
      tertiary: { value: '#565756', description: 'Tertiary text color' },
    },
    link: {
      default: { value: 'var(--color-brand-primary)', description: 'Default link color' },
      hover: { value: 'var(--color-brand-primary)', description: 'Hover link color' },
      visited: { value: 'var(--color-brand-primary)', description: 'Visited link color' },
      active: { value: 'var(--color-brand-primary)', description: 'Active link color' },
      disabled: { value: 'var(--color-brand-primary)', description: 'Disabled link color' },
      focus: { value: 'var(--color-brand-primary)', description: 'Focus link color' },
      link: { value: 'var(--color-brand-primary)', description: 'Link color' },
      linkHover: { value: 'var(--color-brand-primary)', description: 'Link hover color' },
      linkVisited: { value: 'var(--color-brand-primary)', description: 'Link visited color' },
    },
    background: {
      'bg-brand': { value: '#fad26b', description: 'Brand background color' },
      'bg-disabled': { value: 'var(--color-gray-800)', description: 'Disabled background color' },
      'bg-primary': { value: 'var(--color-base-black)', description: 'Primary background color' },
      'bg-secondary': { value: 'var(--color-gray-950)', description: 'Secondary background color' },
      'bg-surface': { value: '#f3f3f312', description: 'Surface background color' },
      'bg-surface-new': { value: '#f3f3f312', description: 'New surface background color' },
      'bg-tertiary': { value: 'var(--color-brand-secondary)', description: 'Tertiary background color' },
      'bg-page': { value: 'var(--color-base-black)', description: 'Page background color' },
      'bg-table': { value: '#1b1b1b', description: 'Table background color' },
      'bg-accent': { value: '#423d31', description: 'Pagination highlighted color' },
      'input-input-default': { value: '#0b0b0b', description: 'Default input background' },
    },
    border: {
      'border-disabled': { value: 'var(--color-gray-950)', description: 'Disabled border color' },
      'border-error': { value: 'var(--color-red-600)', description: 'Error border color' },
      'border-focus': { value: 'var(--color-gray-50)', description: 'Focus border color' },
      'border-primary': { value: 'var(--color-gray-950)', description: 'Primary border color' },
      'border-secondary': { value: 'var(--color-gray-800)', description: 'Secondary border color' },
    },
    button: {
      'button-border-radius': { value: '9999px', description: 'Button border radius for medium/large' },
      'button-border-radius-small': { value: '4px', description: 'Button border radius for small/tiny' },
      'button-border-radius-full': { value: '999px', description: 'Button border radius full' },
      'button-disabled': { value: 'var(--color-gray-950)', description: 'Disabled button color' },
      'button-primary': { value: 'var(--color-brand-primary)', description: 'Primary button color' },
      'button-color-primary': { value: 'var(--text-inverse)', description: 'Primary button color - Purple' },
      'button-primary-hover': { value: '#fce19c', description: 'Primary button hover color' },
      'button-secondary': { value: 'var(--text-primary)', description: 'Secondary button color' },
      'button-color-secondary': { value: 'var(--black)', description: 'Secondary button color - Black' },
      'button-secondary-hover': { value: '#2f2f46', description: 'Secondary button hover color' },
      'button-tertiary': { value: '#f3f3f31a', description: 'Tertiary button color' },
      'button-tertiary-hover': { value: '#f3f3f326', description: 'Tertiary button hover color' },
      'button-outline': { value: 'transparent', description: 'Outline button background' },
      'button-ghost': { value: 'transparent', description: 'Ghost button background' },
    },
    common: {
      'primary-color-hover': { value: '#ffffff', description: 'Primary color hover state' },
      'primary-color-pressed': { value: '#ffffff', description: 'Primary color pressed state' },
      'primary-color-suppl': { value: '#ffffff', description: 'Primary color supplement' },
      'skeleton-color': { value: '#1B1B1B', description: 'Skeleton background color' },
      'success-color': { value: '#22C55E', description: 'Common success color' },
      'warning-color': { value: '#FB923C', description: 'Common warning color' },
    },
  } satisfies SemanticTokens,
  components: {
    BaseDropdown: {
      nameColor: 'var(--text-primary)',
      nameColorHover: 'var(--text-primary)',
      nameBorderColorHover: 'var(--border-border-focus)',
      nameBackgroundColor: 'var(--background-bg-surface)',
      iconColor: 'var(--text-content)',
      iconColorHover: 'var(--text-content)',
      menuBackgroundColor: 'var(--background-bg-secondary)',
      menuItemColor: 'var(--text-primary)',
      menuItemBorderColor: 'var(--border-border-secondary)',
      menuItemSelectedBackgroundColor: 'var(--background-bg-disabled)',
      menuItemSelectedColor: 'var(--text-content)',
      menuItemBackgroundColorHover: 'var(--background-bg-disabled)',
    },
    BaseCheckbox: {
      color: 'var(--border-border-primary)',
      colorChecked: 'var(--color-brand-primary)',
    },
    BaseShopList: {
      headingColor: 'var(--text-primary)',
    },
    BaseSortItem: {
      iconColor: 'var(--text-primary)',
    },
    Pagination: {
      itemBackgroundColor: 'var(--background-bg-primary)',
      itemColor: 'var(--text-primary)',
      itemActiveBackgroundColor: 'var(--background-bg-tertiary)',
      itemActiveColor: 'var(--text-primary)',
    },
    SearchInput: {
      iconColor: 'var(--text-content)',
      placeholderColor: 'var(--text-secondary)',
      textColor: 'var(--text-content)',
    },
    DynamicSearch: {
      backgroundColor: 'var(--background-bg-secondary)',
      searchPageBorderColor: 'var(--background-bg-secondary)',
      itemUserNameColor: 'var(--text-primary)',
      itemUserPositionColor: 'var(--text-content)',
      itemContainerBackgroundColorHover: 'var(--background-bg-surface)',
      itemUserPositionColorHover: 'var(--text-content)',
    },
    DragNDrop: {
      backgroundColor: 'var(--background-bg-secondary)',
    },

    header: {
      common: {
        backgroundColor: 'var(--background-bg-secondary)',
      },
      search: {
        backgroundColor: 'var(--background-bg-surface)',
        placeholderColor: 'var(--text-secondary)',
        iconColor: 'var(--text-primary)',
        borderColor: 'var(--border-border-secondary)',
      },
      menu: {
        backgroundColor: 'var(--background-bg-surface)',
        menuIconColor: 'var(--text-primary)',
        menuColor: 'var(--text-primary)',
        listBackgroundColor: 'var(--background-bg-secondary)',
        listItemColor: 'var(--text-primary)',
      },
    },
    sidebar: {
      containerBackgroundColor: 'var(--component-header-common-background-color)',
      itemActiveBackgroundColor: 'var(--background-bg-tertiary)',
      itemHoverBackgroundColor: 'var(--background-bg-secondary)',
      itemIconColor: 'var(--text-content)',
      itemIconHoverColor: 'var(--text-content)',
      itemIconActiveColor: 'var(--text-primary)',
      groupColor: 'var(--text-primary)',
      itemBackgroundColor: 'var(--purple-light)',
      itemColor: 'var(--text-content)',
      borderRight: '1px solid var(--border-border-primary)',
      itemActiveColor: 'var(--text-primary)',
      itemHoverColor: 'var(--text-content)',
    },
    home: {
      // Common
      cardCommonBackgroundColor: 'var(--background-bg-surface)',
      cardCommonLinkColor: 'var(--text-primary)',
      cardCommonLinkIcon: {
        backgroundColor: 'var(--color-brand-primary)',
        arrowColor: 'var(--text-inverse)',
      },
      // Faq
      cardFaqColor: 'var(--text-primary)',
      cardFaqBackgroundColor: 'var(--background-bg-surface)',

      // Feedback
      cardFeedbackBackgroundColor: 'var(--background-bg-surface)',
      cardFeedbackBackgroundImage: 'none',

      // User
      cardUserNameColor: 'var(--text-primary)',
      cardUserContactKeyColor: 'var(--text-content)',
      // Balance
      cardBalanceBackgroundColor: 'var(--background-bg-surface)',
      cardBalanceColor: 'var(--text-primary)',
      cardBalanceHintColor: 'var(--text-primary)',
      cardBalanceCoinsIcon: winfinityCoinIcon,

      // Birthdays
      cardBirthdaysBackgroundColor: 'var(--background-bg-surface)',
      cardBirthdaysLinkColor: 'var(--text-primary)',
      cardBirthdaysUserPositionColor: 'var(--text-content)',
    },
    cart: {
      containerBackgroundColor: 'var(--background-bg-surface)',
      nameColor: 'var(--text-primary)',
      detailsColor: 'var(--text-content)',
      priceColor: 'var(--text-primary)',
    },
    shopItem: {
      nameColor: 'var(--text-primary)',
      noItemsColor: 'var(--text-primary)',
      priceColor: 'var(--text-primary)',
      actionsColor: 'var(--text-content)',
      swiperNavigationColor: 'var(--text-primary)',
      buyCounterColor: 'var(--text-secondary)',
      buyCounterBaseColor: 'var(--text-content)',
      balanceBadgeAmountColor: 'var(--text-content)',
      cartBadgeIconColor: 'var(--text-content)',
      attributeNameColor: 'var(--text-primary)',
      attributeDescriptionColor: 'var(--text-primary)',
      attributeValueBorderColor: 'var(--text-primary)',
      attributeValueTextColor: 'var(--text-primary)',
      attributeValueSelectedColor: 'var(--text-primary)',
      actionBarTitleColor: 'var(--text-primary)',
      quantityControlColor: 'var(--text-primary)',
      quantityCountColor: 'var(--text-primary)',
    },
    tagsSelector: {
      tagBackgroundColor: 'transparent',
      tagBorderColor: 'var(--border-border-secondary)',
      tagColor: 'var(--text-content)',
      tagSelectedBackgroundColor: 'var(--background-bg-tertiary)',
      tagSelectedBorderColor: 'var(--border-border-primary)',
      tagSelectedColor: 'var(--text-primary)',
    },
    lightbox: {
      slideBackgroundColor: 'transparent',
      navigationColor: 'var(--text-primary)',
      closeButtonColor: 'var(--text-primary)',
    },
    shopGallery: {
      slideBackgroundColor: 'transparent',
      thumbBackgroundColor: 'transparent',
      navigationColor: 'var(--text-primary)',
    },
    createShopItem: {
      formWrapperBackgroundColor: 'transparent',
      formFieldBackgroundColor: 'var(--background-bg-surface)',
      formFieldBorderColor: 'var(--border-border-primary)',
      formFieldBorderColorFocus: 'var(--border-border-focus)',
      formFieldColor: 'var(--text-primary)',
      formFieldPlaceholderColor: 'var(--text-secondary)',
      formFieldLabelColor: 'var(--text-primary)',
      formFieldIconColor: 'var(--text-primary)',
      controlLinkColor: 'var(--text-content)',
      addButtonColor: 'var(--text-content)',
      editorBackgroundColor: 'var(--background-bg-surface)',
      editorBorderColor: 'var(--border-border-primary)',
      editorTextColor: 'var(--text-primary)',
      editorControlColor: 'var(--text-primary)',
      stockControlBackgroundColor: 'var(--background-bg-secondary)',
      stockControlBorderColor: 'var(--border-border-secondary)',
      stockControlHeadingColor: 'var(--text-primary)',
      stockControlTextColor: 'var(--text-primary)',
      stockControlScrollbarTrackColor: 'var(--background-bg-surface)',
      stockControlScrollbarThumbColor: 'var(--border-border-secondary)',
    },
    feedback: {
      formBackgroundColor: 'transparent',
      inputBackgroundColor: 'var(--background-bg-surface)',
      inputBorderColor: 'var(--border-border-primary)',
      inputTextColor: 'var(--text-primary)',
      inputPlaceholderColor: 'var(--text-secondary)',
      dropdownMenuBackgroundColor: 'var(--background-bg-secondary)',
      dropdownMenuTextColor: 'var(--text-primary)',
      submitButtonBackground: 'var(--button-button-primary)',
      submitButtonColor: 'var(--text-inverse)',
      cancelButtonBackground: 'var(--button-button-tetriary)',
      cancelButtonColor: 'var(--text-content)',
      dropdownIsOutlined: true,
    },
    feedbackControl: {
      sectionBackgroundColor: 'var(--background-bg-surface)',
      sectionHeadingColor: 'var(--text-primary)',
      inputBackgroundColor: 'var(--background-bg-secondary)',
      inputTextColor: 'var(--text-primary)',
      inputPlaceholderColor: 'var(--text-secondary)',
      inputBorderColor: 'var(--border-border-primary)',
      addLinkColor: 'var(--text-primary)',
      deleteLinkColor: 'var(--text-primary)',
      deleteLinkColorHover: 'var(--text-accent)',
      counterColor: 'var(--text-accent)',
      scrollbarTrackColor: 'var(--background-bg-secondary)',
      scrollbarThumbColor: 'var(--border-border-primary)',
    },
    balanceHistory: {
      containerBackgroundColor: 'var(--background-bg-surface)',
      infoBaseColor: 'var(--text-primary)',
      tabsContentColor: 'var(--text-primary)',
      infoDateColor: 'var(--text-primary)',
      infoPriceColor: 'var(--text-primary)',
      tabsIndicatorColor: 'var(--border-border-focus)',
    },
    badge: {
      coinIcon: winfinityCoinIcon,
    },
    baseDialog: {
      containerBackgroundColor: 'var(--background-bg-secondary)',
      textColor: 'var(--text-primary)',
      confirmButtonBackgroundColor: 'var(--button-button-primary)',
      confirmButtonTextColor: 'var(--text-inverse)',
      confirmButtonBackgroundColorHover: 'var(--button-button-primary-hover)',
      cancelButtonBackgroundColor: 'var(--button-button-tertiary)',
      cancelButtonTextColor: 'var(--text-content)',
      cancelButtonBackgroundColorHover: 'var(--button-button-tertiary-hover)',
      cancelButtonTextColorHover: 'var(--text-content)',
      backdropBackgroundColor: 'rgba(26, 26, 26, 0.70)',
    },
    auth: {
      containerBackgroundColor: 'var(--background-bg-page)',
      logoContainerBackground: `url(${winfinityBg}) no-repeat center / cover`,
      titleWelcomeColor: 'var(--text-primary)',
      titleCorpColor: 'var(--text-primary)',
    },
    profiles: {
      contentBackgroundColor: 'var(--background-bg-surface)',
      titleColor: 'var(--text-base)',
      lineKeyColor: 'var(--text-secondary)',
      // Thanks dialog
      thanksDialogBackgroundColor: 'var(--background-bg-secondary)',
      thanksDialogImageBackground: `url(${winfinityBg}) no-repeat center / cover`,
      thanksDialogInputBackgroundColor: 'var(--background-bg-surface)',
      thanksDialogInputColor: 'var(--text-primary)',
      thanksDialogInputBorderColor: 'var(--border-border-primary)',
      thanksDialogInputBorderColorFocus: 'var(--border-border-focus)',
      thanksDialogAmountInputColor: 'var(--text-content)',
      thanksDialogCategoryInputListItemBackgroundColorHover: 'var(--color-gray-600)',
      thanksDialogSubmitButtonType: 'primary',
      thanksDialogCancelButtonType: 'tertiary',
      thanksDialogFieldLabelColor: 'var(--text-primary)',

      // ProfilesListItem
      profilesItemBackgroundColor: 'var(--background-bg-surface)',
      profilesItemNameColor: 'var(--text-primary)',
      profilesItemDescriptionColor: 'var(--text-secondary)',

      // ProfilesListSearchBar
      profilesListSearchBarBorderColor: 'var(--background-bg-surface)',
    },
    allocateCoins: {
      formFieldColor: 'var(--text-primary)',
      formFieldPlaceholderColor: 'var(--text-secondary)',
      formFieldLabelColor: 'var(--text-primary)',
      formFieldBorderColor: 'var(--border-border-primary)',
      formFieldBorderColorFocus: 'var(--border-border-focus)',
      formFieldBackgroundColor: 'var(--background-bg-surface)',
      formFieldAmountCoinIcon: winfinityCoinIcon,
      formFieldAmountCoinIconColor: 'var(--text-content)',
      formFieldAmountColor: 'var(--text-primary)',
      formFieldCommentColor: 'var(--text-primary)',
      formFieldNamePopupBackgroundColor: 'var(--background-bg-secondary)',
      formFieldNamePopupColor: 'var(--text-primary)',
      formFieldNameInputColor: 'var(--text-primary)',
      formFieldNameDropdownIconColor: 'var(--text-primary)',
      formButtonSendType: 'primary',
      formButtonResetType: 'tertiary',
      formFieldNameChipColor: 'var(--text-primary)',
      formFieldNameChipBackgroundColor: 'var(--background-bg-secondary)',
      formFieldNameChipRemoveColor: 'var(--text-primary)',
      formFieldCategoryItemBackgroundColorHover: 'var(--background-bg-surface)',

      bulkModalOpenButtonType: 'tertiary',
      tabsItemContentColor: 'var(--text-content)',
      tabsItemActiveContentColor: 'var(--text-primary)',
      tabsItemIndicatorColor: 'var(--border-border-focus)',
    },
    languageSwitcher: {
      textColor: 'var(--text-primary)',
    },
    tagsControl: {
      sectionBackgroundColor: 'var(--background-bg-surface)',

      addItemButtonColor: 'var(--text-content)',
    },
    orders: {
      paginationWrapperAfterBackgroundColor: 'var(--border-border-primary)',

      // OrdersList
      listOrdersFilterPopupBackgroundColor: 'var(--background-bg-secondary)',
      listOrdersFilterPopupColor: 'var(--text-primary)',
      listItemInfoBackgroundColor: 'var(--background-bg-surface)',
      listItemInfoNumberColor: 'var(--text-primary)',
      listItemInfoReddyIconColorHover: 'var(--text-primary)',
      listItemInfoStateBackgroundColor: 'var(--background-bg-surface)',
      listItemInfoStateColor: 'var(--text-primary)',
      listItemInfoStateSelectedBackgroundColor: 'var(--background-bg-surface)',
      listItemInfoStateSelectedColor: 'var(--text-primary)',
      listItemInfoStateMenuOptionsBackgroundColor: 'transparent',
      listItemInfoStateMenuOptionBackgroundColorHover: 'var(--background-bg-surface)',

      // OrderProduct
      orderProductInfoNameColor: 'var(--text-primary)',
      orderProductInfoAttributesColor: 'var(--text-content)',
      orderProductInfoQuantityColor: 'var(--text-primary)',
    },
    rolesControl: {
      actionsBarHeadingColor: 'var(--text-primary)',
      actionsBarFiltersBackgroundColor: 'var(--background-bg-primary)',
      actionsBarFiltersColorHover: 'var(--border-border-focus)',
      actionsBarFiltersMenuBackgroundColor: 'var(--background-bg-secondary)',

      roleDropdownListColor: 'var(--text-primary)',
      roleDropdownListItemBackgroundColorHover: 'var(--background-bg-surface)',
      roleDropdownListItemSelectedBackgroundColor: 'var(--background-bg-surface)',

      listItemBackgroundColor: 'var(--background-bg-surface)',
      listItemInfoNameColor: 'var(--text-primary)',
      listItemRoleDropdownBackgroundColor: 'var(--background-bg-surface)',
      listItemRoleDropdownColorHover: 'var(--border-border-focus)',
      listItemRoleDropdownMenuBackgroundColor: 'var(--background-bg-secondary)',

      listPaginationWrapperAfterBackgroundColor: 'var(--border-border-primary)',
    },
    // TODO: temporary solution, replace with common semantic tokens
    transactions: {
      borderPrimary: 'var(--border-border-primary)',
      filterBackground: 'var(--background-bg-secondary)',
      footerBackground: 'transparent',
    },
  },
}
